import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight, ArrowRight, Scale, ShieldCheck, Gavel } from "lucide-react";
import { MaskedReveal, FadeUp, ClipImage } from "@/components/motion/Reveal";
import { Marquee } from "@/components/motion/Marquee";
import { FIRM, PRACTICE_AREAS } from "@/data/site";
import Clients from "@/components/sections/Clients";
import People from "@/components/sections/People";

/* ---------- HERO ---------- */
function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  // subtle 3D tilt following mouse
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), { stiffness: 120, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-4, 4]), { stiffness: 120, damping: 20 });

  const onMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} className="relative min-h-[100svh] pt-32 pb-16 overflow-hidden" data-testid="hero">
      {/* Top meta bar */}
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex items-center justify-between mono text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-[hsl(var(--muted-foreground))]">
        <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>Mumbai and Pune</motion.span>
        <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>24×7 Bail Line</motion.span>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 md:px-10 mt-10 md:mt-14 grid grid-cols-12 gap-6 md:gap-10">
        {/* Left column — big headline */}
        <motion.div style={{ y: yText }} className="col-span-12 lg:col-span-8 relative">
          <div className="mono text-xs uppercase tracking-[0.32em] text-[hsl(var(--muted-foreground))] mb-4 flex items-center gap-3">
            <span className="text-gold">✦</span> Chapter 00 · The Firm
          </div>
          <MaskedReveal
            as="h1"
            className="serif font-light tracking-[-0.02em] leading-[0.94] text-[9vw] md:text-[6.6vw] lg:text-[5.4vw]"
            lines={["Experienced legal", <>counsel for <em className="italic text-gold">individuals,</em></>, "Families & Businesses."]}
            nowrap
            stagger={0.14}
            delay={0.35}
            testid="hero-headline"
          />
          <FadeUp delay={1.1} className="mt-10 max-w-xl text-[hsl(var(--muted-foreground))] leading-relaxed text-[15px]">
            Helkar & Associates provides practical legal solutions in Family Law, Criminal Defence, Civil Litigation, Corporate Advisory, Testamentary matters, Cooperative Housing Society, DRT and MACT — from our chambers in Kurla West, Mumbai.
          </FadeUp>

          <FadeUp delay={1.25} className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/contact" className="btn-ink" data-testid="hero-book">Book Consultation <ArrowUpRight className="h-4 w-4" /></Link>
            <a href={`tel:${FIRM.phoneRaw}`} className="btn-ghost" data-testid="hero-call">Call Now</a>
            <a href={FIRM.whatsapp} target="_blank" rel="noreferrer" className="editorial-link text-sm uppercase tracking-[0.22em]" data-testid="hero-whatsapp">WhatsApp Us <ArrowRight className="h-4 w-4" /></a>
          </FadeUp>
        </motion.div>

        {/* Right column — image with tilt & clip */}
        <motion.div
          onMouseMove={onMouse}
          onMouseLeave={() => { mx.set(0); my.set(0); }}
          style={{ rotateX: rx, rotateY: ry, perspective: 1000 }}
          className="col-span-12 lg:col-span-4 relative"
        >
          <div className="relative aspect-[3/4] overflow-hidden hairline border">
            <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0">
              <img
                src="https://images.unsplash.com/photo-1589578527966-fdac0f44566c?q=80&w=1400&auto=format&fit=crop"
                alt="Advocate at chambers"
                className="w-full h-full object-cover"
              />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--navy))]/50 via-transparent to-transparent" />
            <div className="absolute inset-x-4 bottom-4 flex items-end justify-between text-ivory">
              <div>
                <div className="mono text-[10px] uppercase tracking-[0.28em] opacity-80">Adv. Prasad Helkar</div>
                <div className="serif italic text-lg">Managing Advocate</div>
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))]">
            <span>Kurla West · Mumbai</span>
            <span>Bombay High Court</span>
          </div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 mt-16 flex items-end justify-between">
        <FadeUp delay={1.4} className="flex items-center gap-3 mono text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--muted-foreground))]">
          <span className="inline-block h-8 w-px bg-[hsl(var(--foreground))]/40" />
          Scroll to explore
        </FadeUp>
        <FadeUp delay={1.5} className="hidden md:flex items-center gap-6 text-xs">
          <span className="flex items-center gap-2"><Scale className="h-4 w-4 text-gold" /> 8 years of practice</span>
          <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-gold" /> Confidential counsel</span>
          <span className="flex items-center gap-2"><Gavel className="h-4 w-4 text-gold" /> Trial-tested</span>
        </FadeUp>
      </div>
    </section>
  );
}

/* ---------- MANIFESTO ---------- */
function Manifesto() {
  const chapters = [
    { n: "I.", title: "Ethics over shortcuts", body: "We refuse the expedient path when it compromises client interest. Every strategy is measured against the standards of the Bar Council of India and our own conscience." },
    { n: "II.", title: "Preparation is representation", body: "Cases are won long before the hearing. We invest in evidence maps, precedent research and mock arguments so that our courtroom is a rehearsed performance — not an improvisation." },
    { n: "III.", title: "Plain-language counsel", body: "We speak with clients in the language they understand and reserve legal Latin for the pleadings. Clarity is a form of respect." },
    { n: "IV.", title: "Discretion, always", body: "Family, criminal and commercial matters demand privacy. Our chambers, our servers and our conversations are governed by a strict confidentiality protocol." },
  ];

  return (
    <section className="relative py-28 md:py-40" data-testid="manifesto">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
        <div className="col-span-12 md:col-span-4 md:sticky md:top-32 self-start">
          <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-6">Chapter 01 · Manifesto</div>
          <MaskedReveal as="h2" className="serif text-5xl md:text-6xl leading-[0.98]" lines={["A firm built on", <>a <em className="italic text-gold">quieter</em></>, "kind of rigour."]} />
          <FadeUp delay={0.3} className="mt-8 text-[hsl(var(--muted-foreground))] max-w-sm">
            Four principles guide the counsel, drafting and courtroom conduct at Helkar & Associates.
          </FadeUp>
        </div>

        <div className="col-span-12 md:col-span-8">
          <div className="divide-y hairline border-y hairline">
            {chapters.map((c, i) => (
              <FadeUp key={c.n} delay={i * 0.08} className="grid grid-cols-12 gap-6 py-10 group">
                <div className="col-span-2 chapter-num text-3xl md:text-5xl">{c.n}</div>
                <div className="col-span-10 md:col-span-8">
                  <h3 className="serif text-2xl md:text-3xl mb-3 group-hover:text-gold transition-colors">{c.title}</h3>
                  <p className="text-[hsl(var(--muted-foreground))] leading-relaxed text-[15px] max-w-xl">{c.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- PRACTICE GRID (editorial) ---------- */
function PracticeShowcase() {
  return (
    <section className="relative py-24 md:py-32 bg-[hsl(var(--ivory-deep))]" data-testid="practice-grid">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex items-end justify-between mb-16">
          <div>
            <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-6">Chapter 02 · Practice</div>
            <MaskedReveal as="h2" className="serif text-5xl md:text-7xl leading-[0.96]" lines={["Every practice.", <>One <em className="italic text-gold">discipline.</em></>]} />
          </div>
          <Link to="/practice-areas" className="hidden md:inline-flex editorial-link text-sm uppercase tracking-[0.22em]">
            All Practice Areas <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
          {PRACTICE_AREAS.map((p, i) => (
            <Link key={p.slug} to={`/practice-areas/${p.slug}`} className="group block" data-testid={`practice-card-${p.slug}`}>
              <div className="relative overflow-hidden aspect-[4/5] mb-5 hairline border">
                <motion.img
                  src={p.hero} alt={p.title}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.9, ease: [0.7, 0.05, 0.2, 1] }}
                />
                <div className="absolute inset-0 bg-[hsl(var(--navy))]/25 group-hover:bg-[hsl(var(--navy))]/45 transition-colors" />
                <div className="absolute inset-x-5 top-5 flex items-center justify-end text-ivory">
                  <ArrowUpRight className="h-4 w-4 -translate-x-1 translate-y-1 group-hover:translate-x-0 group-hover:translate-y-0 transition-transform" />
                </div>
                <div className="absolute inset-x-5 bottom-5 text-ivory">
                  <div className="serif italic text-2xl md:text-3xl leading-tight">{p.title}</div>
                </div>
              </div>
              <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed max-w-sm">{p.lede}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- APPROACH / NUMBERS ---------- */
function Numbers() {
  const stats = [
    { k: "500+", l: "Matters advised" },
    { k: "24×7", l: "Bail assistance" },
    { k: "17", l: "Practice verticals" },
    { k: "PAN-IN", l: "Representation" },
  ];
  return (
    <section className="relative py-24 md:py-32" data-testid="numbers">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
        {stats.map((s, i) => (
          <FadeUp key={s.l} delay={i * 0.1}>
            <div className="serif text-6xl md:text-7xl leading-none">{s.k}</div>
            <div className="mono text-[11px] uppercase tracking-[0.3em] mt-4 text-[hsl(var(--muted-foreground))]">{s.l}</div>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

/* ---------- CLIENT VOICES ---------- */
function Voices() {
  const items = [
    { q: "Their bail counsel is genuinely 24×7. We were represented within hours of a midnight FIR.", who: "Managing Director, Logistics Co." },
    { q: "The team combines commercial pragmatism with courtroom craft. Our M&A closed on schedule.", who: "Founder, D2C Brand" },
    { q: "Handled a difficult probate with sensitivity and precision. The family remained intact.", who: "Executor, Estate matter" },
  ];
  return (
    <section className="relative py-24 md:py-32 bg-navy text-ivory overflow-hidden" data-testid="voices">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
        <div className="col-span-12 md:col-span-4">
          <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-6">Chapter 04 · Client Voices</div>
          <MaskedReveal as="h2" className="serif text-5xl md:text-6xl leading-[0.98]" lines={["What our", <em className="italic text-gold">clients</em>, "say."]} />
        </div>
        <div className="col-span-12 md:col-span-8 space-y-10">
          {items.map((t, i) => (
            <FadeUp key={i} delay={i * 0.1} className="border-t border-white/10 pt-8 first:border-t-0 first:pt-0">
              <p className="serif italic text-2xl md:text-3xl leading-snug">“{t.q}”</p>
              <div className="mt-4 mono text-[11px] uppercase tracking-[0.28em] text-ivory/60">— {t.who}</div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- CTA ---------- */
function CTA() {
  return (
    <section className="relative py-28 md:py-40" data-testid="home-cta">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 text-center">
        <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-8">Chapter 06 · Retain Counsel</div>
        <MaskedReveal
          as="h2"
          className="serif text-6xl md:text-[9vw] leading-[0.9]"
          lines={[<>Let us take the <em className="italic text-gold">first meeting.</em></>]}
        />
        <FadeUp delay={0.3} className="mt-10 max-w-lg mx-auto text-[hsl(var(--muted-foreground))]">
          A candid, confidential assessment of your matter — with a written note on strategy, timelines and estimated costs.
        </FadeUp>
        <FadeUp delay={0.4} className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <Link to="/contact" className="btn-gold" data-testid="cta-book">Book Consultation</Link>
          <a href={FIRM.whatsapp} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="cta-whatsapp">WhatsApp Us</a>
        </FadeUp>
      </div>
    </section>
  );
}

export default function Home() {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <div data-testid="home-page" className={loaded ? "" : "opacity-0"}>
      <Hero />
      <Marquee items={["Committed to Justice", "Dedicated to Excellence", "Established in Mumbai", "Family · Criminal · Corporate", "Civil · Testamentary · DRT · MACT"]} />
      <Manifesto />
      <PracticeShowcase />
      <People chapter="Chapter 03 · The People" className="bg-[hsl(var(--ivory-deep))]" />
      <Numbers />
      <Voices />
      <Clients />
      <CTA />
    </div>
  );
}
